import { ARMOR_SLOT_ORDER } from "./vipArmorSlots";
import {
    createKitPlacementFromTemplate,
    createPlacementFromCatalogEntry,
    getCatalogEntry,
} from "./vipSimulatorCatalog";

/**
 * @param {string} folder
 * @param {string} file
 */
export function catalogIdFromFile(folder, file) {
    return `${folder}--${String(file).replace(/\\/g, "/")}`;
}

/**
 * @typedef {Object} QuickEquipPreset
 * @property {string} id
 * @property {{ pt: string, en: string }} label — resolver com `l()` / `localize()`
 * @property {Record<string, string>} slots — accept → catalogId
 */

/** @type {QuickEquipPreset[]} */
export const QUICK_EQUIP_PRESETS = [
    {
        id: "metal",
        label: { pt: "Full 7 metal", en: "Full 7 metal" },
        slots: {
            "armor-mask": catalogIdFromFile("Roupas", "Máscara de Metal.webp"),
            "armor-chest": catalogIdFromFile("Roupas", "Colete de Metal.webp"),
            "armor-legs": catalogIdFromFile("Roupas", "Calça.webp"),
            "armor-head": catalogIdFromFile("Roupas", "Blusão.webp"),
            "armor-feet": catalogIdFromFile("Roupas", "Botas.webp"),
            "armor-hands": catalogIdFromFile("Roupas", "Luvas Táticas.png"),
            "armor-belt": catalogIdFromFile("Roupas", "Saia de Placas de Trânsito.webp"),
        },
    },
    {
        id: "roadsign",
        label: { pt: "Full 7 normal", en: "Full 7 road sign" },
        slots: {
            "armor-belt": catalogIdFromFile("Roupas", "Saia de Placas de Trânsito.webp"),
            "armor-chest": catalogIdFromFile("Roupas", "Jaqueta de Placas de Trânsito.webp"),
            "armor-head": catalogIdFromFile("Roupas", "Capacete de Lata de Café.webp"),
            "armor-hands": catalogIdFromFile("Roupas", "Luvas de Sinalização.png"),
            "armor-legs": catalogIdFromFile("Roupas", "Calça.webp"),
            "armor-mask": catalogIdFromFile("Roupas", "Blusão.webp"),
            "armor-feet": catalogIdFromFile("Roupas", "Botas.webp"),
        },
    },
    {
        id: "wolf",
        label: { pt: "Full 7 lobo", en: "Full 7 wolf" },
        slots: {
            "armor-head": catalogIdFromFile("Roupas", "Cocar de Lobo.webp"),
            "armor-mask": catalogIdFromFile("Roupas", "Blusão.webp"),
            "armor-chest": catalogIdFromFile("Roupas", "Jaqueta.webp"),
            "armor-belt": catalogIdFromFile("Roupas", "Calça.webp"),
            "armor-hands": catalogIdFromFile("Roupas", "Luvas de Couro.webp"),
            "armor-legs": catalogIdFromFile("Roupas", "Calça de Madeira.webp"),
            "armor-feet": catalogIdFromFile("Roupas", "Botas.webp"),
        },
    },
];

/**
 * @param {{ pool: Record<string, { catalogId: string }[]> }} sim
 * @param {string} catalogId
 */
function findPoolPlacementByCatalogId(sim, catalogId) {
    for (const category of Object.keys(sim.pool)) {
        const arr = sim.pool[category];
        for (let i = 0; i < arr.length; i++) {
            if (arr[i]?.catalogId === catalogId) return arr[i];
        }
    }
    return null;
}

/**
 * @param {object} sim
 * @param {string} presetId
 * @returns {{ armor: Record<string, object | null>, missing: string[] } | null}
 */
export function buildQuickEquipArmor(sim, presetId) {
    const preset = QUICK_EQUIP_PRESETS.find((p) => p.id === presetId);
    if (!preset) return null;

    /** @type {Record<string, object | null>} */
    const armor = Object.fromEntries(ARMOR_SLOT_ORDER.map((a) => [a, null]));
    const missing = [];

    for (const accept of ARMOR_SLOT_ORDER) {
        const catalogId = preset.slots[accept];
        if (!catalogId) continue;

        const entry = getCatalogEntry(catalogId);
        if (!entry) {
            missing.push(catalogId);
            continue;
        }

        const poolTemplate = findPoolPlacementByCatalogId(sim, catalogId);
        const placement = poolTemplate
            ? createKitPlacementFromTemplate(poolTemplate)
            : createPlacementFromCatalogEntry(entry);

        if (placement) {
            armor[accept] = placement;
        } else {
            missing.push(entry.name);
        }
    }

    return { armor, missing };
}
